/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiEdit2, FiSearch, FiX, FiEye, FiTrash2, FiBox } from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
// Using colors from useUI instead of hardcoded COLORS

// Helper function to configure Sweet Alert to appear at top
const configureSwalTop = () => {
  return {
    customClass: {
      container: 'swal2-container-top'
    },
    didOpen: () => {
      if (!document.getElementById('swal-top-style')) {
        const style = document.createElement('style');
        style.id = 'swal-top-style';
        style.textContent = `
          .swal2-container-top {
            position: fixed !important;
            top: 20px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            z-index: 20000 !important;   /* ✅ أعلى من المودالات */
          }

          .swal2-backdrop-show {
            z-index: 19999 !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  };
};


export default function AdminTags() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);



  // Note: Search is now handled by ResponsiveTable component
  const filtered = tags;

  // Auto-open Add Modal if coming from AllProducts
  useEffect(() => {
    if (location.state?.openAdd && perms.addCategories) {
      setShowAdd(true);
      // Optional: Clear state so it doesn't re-open on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, perms.addCategories]);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showView, setShowView] = useState(null);
  const [newTag, setNewTag] = useState({ name: "", active: true, products: [] });
  const [allProducts, setAllProducts] = useState([]);

  const productMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => {
      map[p.id] = p.name;
    });
    return map;
  }, [allProducts]);
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${baseUrl}/GetItems?tenantId=${tenantId}&ItemId=-1`, {
          headers: {
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        });

        const data = await res.json();
        const mapped = Array.isArray(data)
          ? data.map(p => ({ id: p.itemId, name: p.itemName }))
          : [];

        setAllProducts(mapped);
      } catch {
        setAllProducts([]);
      }
    };

    if (tenantId) fetchAllProducts();
  }, [tenantId, baseUrl]);

  const fetchTags = React.useCallback(async () => {
    if (!tenantId || tenantId === -1) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${baseUrl}/Tags/get?tenantId=${tenantId}`, {
        headers: {
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      const mappedTags = Array.isArray(data)
        ? data.map(tag => ({
          tagId: tag.tagId,
          name: tag.tagName || "",
          active: tag.isActive === true,
          products: Array.isArray(tag.linkedProductsCount)
            ? tag.linkedProductsCount.map(p => p.itemId)
            : [],
          linkedProductsCount: Array.isArray(tag.linkedProductsCount)
            ? tag.linkedProductsCount.length
            : tag.productsCount || 0
        }))
        : [];



      setTags(mappedTags);
    } catch (e) {
      console.error("fetchTags error:", e);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, baseUrl]);



  // Fetch tags from API
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);
  const buildPayload = (tag) => {
    const finalIds = tag.originalProducts
      ? Array.from(new Set([...tag.originalProducts, ...tag.products]))
      : tag.products;

    const safeProducts = finalIds
      .map(id => {
        const p = allProducts.find(x => x.id === id);
        return p ? { itemId: p.id, itemName: p.name } : null;
      })
      .filter(p => p && p.itemId);

    return {
      tagId: tag.tagId || 0,
      tenantId,
      tagName: tag.name.trim(),
      isActive: tag.active ? 1 : 0,
      products: safeProducts
    };
  };

  const saveTag = async (payload) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      if (payload.products.some(p => !p.itemId)) {
        console.error("INVALID PRODUCTS PAYLOAD:", payload.products);
        throw new Error("Invalid product data detected");
      }

      const res = await fetch(`${baseUrl}/Tags/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      console.log("TAG SAVE RESPONSE:", res.status, text);

      if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

      await fetchTags();

      Swal.fire({
        icon: "success",
        title: payload.tagId === 0 ? (isArabic ? "تمت الإضافة بنجاح" : "Added successfully") : (isArabic ? "تم التعديل بنجاح" : "Updated successfully"),
        timer: 1500,
        showConfirmButton: false,
        ...configureSwalTop()
      });

      setShowAdd(false);
      setShowEdit(null);

      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };


  const handleAdd = async () => {
    if (!perms.addCategories) return;

    const payload = buildPayload({ ...newTag, tagId: 0 });

    await saveTag(payload);
  };

  const handleEditSave = async () => {
    if (!perms.editCategories) return;

    const payload = buildPayload(showEdit);

    await saveTag(payload);
  };




  const handleDelete = async (tag) => {
    if (!perms.deleteCategories) {
      return;
    }
    const result = await Swal.fire({
      icon: "warning",
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? `هل تريد حذف البراند "${tag.name}"؟`
        : `Do you want to delete tag "${tag.name}"?`,
      showCancelButton: true,
      confirmButtonColor: colors.red,
      cancelButtonColor: colors.mainColor,
      confirmButtonText: isArabic ? "حذف" : "Delete",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
      background: isDark ? "#1E293B" : "#fff",
      color: isDark ? "#fff" : "#000",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      const tagId = tag.tagId || tag.id;

      if (!tagId) {
        throw new Error(isArabic ? "معرف البراند غير موجود" : "Tag ID not found");
      }



      console.log("📤 Sending DeleteTag request:", { tenantId, tagId });

      const res = await fetch(
        `${baseUrl}/Tags/delete?tenantId=${tenantId}&tagId=${tagId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        }
      );

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
        }
      } else {
        const text = await res.text();
        if (text) {
          data = { message: text };
        }
      }

      console.log("📥 DeleteTag Response status:", res.status, res.statusText);
      console.log("📥 DeleteTag Response data:", data);

      if (!res.ok) {
        // Show error immediately
        Swal.fire({
          icon: "error",
          title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
          text: data.message || data.error || `HTTP ${res.status}: ${res.statusText}` || (isArabic ? "فشل حذف البراند" : "Failed to delete tag"),
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
          ...configureSwalTop(),
        });
        throw new Error(data.message || data.error || `HTTP ${res.status}: ${res.statusText}`);
      }

      console.log("✅ DeleteTag Response:", data);

      // Refresh tags list immediately before showing success message
      await fetchTags();
      console.log("✅ Tags list refreshed after delete");

      // Show success message after refresh
      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف! ✅" : "Deleted! ✅",
        timer: 1500,
        showConfirmButton: false,
        text: data.message || (isArabic ? "تم حذف البراند بنجاح" : "Tag deleted successfully"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        ...configureSwalTop(),
      });
    } catch (err) {
      console.error("DeleteTag error:", err);

      let errorMessage = err.message || String(err);

      // Handle specific error cases
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("404")) {
        errorMessage = isArabic
          ? "البراند غير موجود"
          : "Tag not found";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
          : "Server error. Please try again later";
      }

      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        ...configureSwalTop(),
      });
    }
  };
  if (!perms.viewCategories) {
    return <NoAccess />;
  }

  return (
    <PageContainer title={isArabic ? "البراندات" : "Product Tags"}>
      {/* ====== الإضافة ====== */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        {perms.addCategories && (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium shadow-md "
            style={{ background: colors.mainColor }}
          >
            <FiPlus /> {isArabic ? "إضافةبراند جديد" : "Add Tag"}
          </button>
        )}
      </div>

      {/* ====== الجدول ====== */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
          <p className="mt-4" style={{ color: colors.subText }}>
            {isArabic ? "جاري تحميل البراندات..." : "Loading tags..."}
          </p>
        </div>
      ) : (
        <ResponsiveTable
          columns={[
            { header: isArabic ? "اسم البراند" : "Name", sortKey: "name", align: "center" },
            { header: isArabic ? "الحالة" : "Status", sortKey: "active", align: "center" },
            { header: isArabic ? "المنتجات المرتبطة" : "Linked Products", align: "center" },
            { header: isArabic ? "عدد المنتجات" : "Count", sortKey: "linkedProductsCount", align: "center" },
            { header: isArabic ? "تحكم" : "Actions", align: "center" },
          ]}
          data={filtered}
          searchable={true}
          sortable={true}
          filterOptions={{
            field: "active",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              { value: "true", label: isArabic ? "فعال" : "Active" },
              { value: "false", label: isArabic ? "غير فعال" : "Inactive" },
            ],
          }}
          renderRow={(t) => (
            <>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {t.name}
              </td>
              <td className="py-3 px-4 text-center">
                <span
                  className={`px-3 py-1 rounded-lg text-xs font-semibold ${t.active
                    ? "text-green-400 bg-green-500/10"
                    : "text-red-400 bg-red-500/10"
                    }`}
                >
                  {t.active ? (isArabic ? "فعال" : "Active") : isArabic ? "غير فعال" : "Inactive"}
                </span>
              </td>
              <td className="py-3 px-4 text-center truncate max-w-[250px]" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {t.products.length > 0
                  ? t.products.map(id => productMap[id]).join(" ، ")
                  : isArabic ? "لا يوجد منتجات" : "No products"}
              </td>
              <td className="py-3 px-4 text-center font-semibold" style={{ color: colors.gold }}>
                {Array.isArray(t.products) ? t.products.length : 0}
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex justify-center items-center gap-2">
                  <button
                    onClick={() => setShowView(t)}
                    className="p-2 rounded-lg text-white"
                    style={{ background: colors.mainColor }}
                    title={isArabic ? "عرض" : "View"}
                  >
                    <FiEye />
                  </button>
                  {perms.editCategories && (
                    <button
                      onClick={() =>
                        setShowEdit({
                          ...t,
                          originalProducts: [...t.products],   // ← snapshot
                          products: [...t.products]
                        })
                      }
                      className="p-2 rounded-lg text-white"
                      style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                      title={isArabic ? "تعديل" : "Edit"}
                    >
                      <FiEdit2 />
                    </button>
                  )}
                  {perms.deleteCategories && (
                    <button
                      onClick={() => handleDelete(t)}
                      className="p-2 rounded-lg text-white"
                      style={{ background: colors.red }}
                      title={isArabic ? "حذف" : "Delete"}
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
          renderCard={(t) => (
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {t.name}
                  </h3>
                  <span
                    className={`inline-block mt-1 px-3 py-1 rounded-lg text-xs font-semibold ${t.active
                      ? "text-green-400 bg-green-500/10"
                      : "text-red-400 bg-red-500/10"
                      }`}
                  >
                    {t.active ? (isArabic ? "فعال" : "Active") : isArabic ? "غير فعال" : "Inactive"}
                  </span>
                </div>
                <span className="px-3 py-1 rounded-lg text-sm font-semibold" style={{ color: colors.gold, backgroundColor: isDark ? "rgba(255,215,0,0.1)" : "rgba(242,180,65,0.1)" }}>
                  {t.linkedProductsCount || 0} {isArabic ? "منتج" : "products"}
                </span>
              </div>
              <div className="text-sm" style={{ color: colors.subText }}>
                <p className="mb-1">
                  <strong>{isArabic ? "المنتجات:" : "Products:"}</strong>{" "}
                  {t.products.length > 0
                    ? t.products.map(id => productMap[id]).join(" ، ")
                    : isArabic ? "لا يوجد منتجات" : "No products"}
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowView(t)}
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.mainColor }}
                >
                  <FiEye className="inline mr-1" />
                  {isArabic ? "عرض" : "View"}
                </button>
                <button
                  onClick={() =>
                    setShowEdit({
                      ...t,
                      originalProducts: [...t.products],   // ← snapshot
                      products: [...t.products]
                    })
                  }
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                >
                  <FiEdit2 className="inline mr-1" />
                  {isArabic ? "تعديل" : "Edit"}
                </button>
                <button
                  onClick={() => handleDelete(t)}
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.red }}
                >
                  <FiTrash2 className="inline mr-1" />
                  {isArabic ? "حذف" : "Delete"}
                </button>
              </div>
            </div>
          )}
        />
      )}

      {/* ====== مودالات ====== */}
      <AnimatePresence>
        {showAdd && (
          <TagModal
            title={isArabic ? "إضافةبراند جديد" : "Add Tag"}
            tag={newTag}
            setTag={setNewTag}
            products={allProducts}
            onSave={perms.addCategories ? handleAdd : null}
            onClose={() => setShowAdd(false)}
            isDark={isDark}
            colors={colors}
            isArabic={isArabic}
            saving={isSaving}
          />

        )}
        {showEdit && (
          <TagModal
            title={isArabic ? "تعديل البراند" : "Edit Tag"}
            tag={showEdit}
            setTag={setShowEdit}
            products={allProducts}
            onSave={perms.editCategories ? handleEditSave : null}
            onClose={() => setShowEdit(null)}
            isDark={isDark}
            colors={colors}
            isArabic={isArabic}
            saving={isSaving}
          />

        )}
      </AnimatePresence>

      {/* ====== مودال العرض ====== */}
      <AnimatePresence>
        {showView && (
          <ViewModal
            tag={showView}
            productMap={productMap}
            onClose={() => setShowView(null)}
            isDark={isDark}
            isArabic={isArabic}
            colors={colors}
          />

        )}
      </AnimatePresence>
    </PageContainer>
  );
}

/* ========= مودال الإضافة / التعديل ========= */
function TagModal({ isArabic, isDark, colors, title, tag, setTag, onSave, onClose, products, saving = false }) {

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "auto");
  }, []);



  const filteredProducts = (Array.isArray(products) ? products : []).filter(p =>
    (p.name || "").toLowerCase().includes(searchProduct.toLowerCase())
  );


  const toggleProduct = (product) => {
    if (!product?.id) return;

    const current = Array.isArray(tag.products) ? tag.products : [];

    setTag({
      ...tag,
      products: current.includes(product.id)
        ? current.filter(id => id !== product.id)
        : [...current, product.id]
    });
  };


  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[95%] sm:w-[90%] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-gray-400 transition">
            <FiX size={22} />
          </button>
        </div>

        <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
          {isArabic ? "اسم البراند" : "Tag Name"}
        </label>
        <input
          type="text"
          value={tag.name}
          disabled={!!tag.originalName}
          onChange={(e) => setTag({ ...tag, name: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 mb-4 focus:outline-none transition-all"
          style={{
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db",
            color: isDark ? "#f1f5f9" : "#111827",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = colors.mainColor;
            e.target.style.boxShadow = `0 0 0 2px ${colors.mainColor}20`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db";
            e.target.style.boxShadow = "none";
          }}
        />

        {/* حالة النشاط */}
        <div className="mb-5" dir={isArabic ? "rtl" : "ltr"}>
          <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
            {isArabic ? "حالة النشاط" : "Status"}
          </label>
          <div
            className="flex items-center"
            style={{
              justifyContent: "flex-start",
              direction: isArabic ? "rtl" : "ltr"
            }}
          >
            <label
              className="flex items-center gap-3 cursor-pointer group"
              style={{
                flexDirection: isArabic ? "row" : "row",
                direction: isArabic ? "rtl" : "ltr"
              }}
            >
              {/* Toggle Switch - Same design as Divisions */}
              <button
                onClick={() => setTag({ ...tag, active: !tag.active })}
                className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
                style={{
                  backgroundColor: tag.active ? colors.mainColor : (isDark ? "#475569" : "#d1d5db")
                }}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${tag.active ? "translate-x-6" : "translate-x-0"
                    }`}
                />
              </button>

              {/* Status Text with Badge Style */}
              <div
                className={`
                  px-4 py-1.5 rounded-lg font-semibold text-sm transition-all duration-300
                  ${tag.active
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20'
                  }
                `}
                style={{
                  backgroundColor: tag.active
                    ? 'rgba(16, 185, 129, 0.1)'
                    : (isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(107, 114, 128, 0.1)'),
                  color: tag.active
                    ? '#059669'
                    : (isDark ? '#94a3b8' : '#4b5563'),
                  borderColor: tag.active
                    ? 'rgba(16, 185, 129, 0.2)'
                    : (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(107, 114, 128, 0.2)'),
                }}
              >
                {tag.active
                  ? (isArabic ? "✓ فعال" : "✓ Active")
                  : (isArabic ? "✗ غير فعال" : "✗ Inactive")
                }
              </div>
            </label>
          </div>
        </div>

        {/* Products Selection */}
        <div className="mb-5" dir={isArabic ? "rtl" : "ltr"}>
          <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
            {isArabic ? "اختيار المنتجات" : "Select Products"}
          </label>

          {/* Search Products */}
          <div className="mb-3 relative">
            <input
              type="text"
              placeholder={isArabic ? "ابحث عن منتج..." : "Search products..."}
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none transition-all"
              style={{
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db",
                color: isDark ? "#f1f5f9" : "#111827",
                paddingLeft: "12px",
                paddingRight: "12px",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.mainColor;
                e.target.style.boxShadow = `0 0 0 3px ${colors.mainColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Selected Products Count Badge */}
          {Array.isArray(tag.products) && tag.products.length > 0 && (
            <div
              className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{
                backgroundColor: isDark ? "rgba(116, 0, 191, 0.2)" : "rgba(116, 0, 191, 0.1)",
                color: colors.mainColor,
                border: `1px solid ${colors.mainColor}40`,
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.mainColor }}></span>
              {isArabic
                ? `${tag.products.length} منتج محدد`
                : `${tag.products.length} product(s) selected`}

            </div>
          )}

          {/* Products List */}
          <div
            className="border rounded-xl overflow-hidden shadow-inner"
            style={{
              borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#e5e7eb",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f9fafb",
              maxHeight: "280px",
            }}
          >
            {loadingProducts ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
                <p className="mt-3 text-sm font-medium" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                  {isArabic ? "جاري تحميل المنتجات..." : "Loading products..."}
                </p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="p-2 space-y-1 max-h-[280px] overflow-y-auto">
                {filteredProducts.map((product) => {
                  const isSelected = Array.isArray(tag.products) && tag.products.includes(product.id);
                  return (
                    <label
                      key={product.id}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all group"
                      style={{
                        backgroundColor: isSelected
                          ? (isDark ? "rgba(116, 0, 191, 0.25)" : "rgba(116, 0, 191, 0.15)")
                          : "transparent",
                        border: isSelected
                          ? `1px solid ${colors.mainColor}50`
                          : `1px solid transparent`,
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <div className="relative flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProduct(product)}
                          className="sr-only"
                        />
                        <div
                          className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
                          style={{
                            borderColor: isSelected ? colors.mainColor : (isDark ? "rgba(255, 255, 255, 0.3)" : "#d1d5db"),
                            backgroundColor: isSelected ? colors.mainColor : "transparent",
                          }}
                        >
                          {isSelected && (
                            <svg
                              className="w-3.5 h-3.5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span
                        className="flex-1 text-sm font-medium"
                        style={{
                          color: isSelected
                            ? (isDark ? "#f1f5f9" : "#111827")
                            : (isDark ? "#cbd5e1" : "#4b5563")
                        }}
                      >
                        {product.name}
                      </span>
                      {isSelected && (
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: colors.mainColor }}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="mb-2">
                  <FiBox
                    size={32}
                    style={{ color: isDark ? "#475569" : "#9ca3af" }}
                    className="mx-auto opacity-50"
                  />
                </div>
                <p className="text-sm font-medium" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                  {searchProduct
                    ? (isArabic ? "لا توجد منتجات تطابق البحث" : "No products match your search")
                    : (isArabic ? "لا توجد منتجات متاحة" : "No products available")}
                </p>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          disabled={saving || !onSave}
          aria-busy={saving}
          onClick={() => {
            if (saving || !onSave) return;
            onSave();
          }}
          className="w-full py-2 mt-2 rounded-lg text-white font-semibold shadow-md disabled:opacity-70 disabled:cursor-wait"
          style={{
            background: colors.mainColor,
            pointerEvents: saving ? "none" : "auto",
            cursor: saving ? "wait" : "pointer",
          }}
        >
          {saving ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
        </button>
      </div>
    </motion.div>
  );
}

/* ========= مودال العرض ========= */
function ViewModal({ tag, productMap, onClose, isDark, isArabic, colors }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "auto");
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[95%] sm:w-[90%] max-w-md rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
            {isArabic ? "تفاصيل البراند" : "Tag Details"}
          </h2>
          <button onClick={onClose} className="text-gray-400 transition">
            <FiX size={22} />
          </button>
        </div>

        <p className="mb-2">
          <strong>{isArabic ? "اسم البراند:" : "Name:"}</strong> {tag.name}
        </p>
        <p className="mb-2">
          <strong>{isArabic ? "الحالة:" : "Status:"}</strong>{" "}
          <span className={tag.active ? "text-green-500 font-semibold" : "text-red-500 font-semibold"}>
            {tag.active ? (isArabic ? "فعال" : "Active") : isArabic ? "غير فعال" : "Inactive"}
          </span>
        </p>
        <p className="mb-2">
          <strong>{isArabic ? "عدد المنتجات:" : "Products Count:"}</strong> {tag.products.length}
        </p>

        <div className="mt-4">
          <strong className="block mb-2">
            {isArabic ? "المنتجات المرتبطة:" : "Linked Products:"}
          </strong>
          {Array.isArray(tag.products) && tag.products.length > 0 ? (
            <ul className="list-disc pl-6">
              {tag.products.map((id, i) => (
                <li key={i}>{productMap[id]}</li>
              ))}



            </ul>
          ) : (
            <p>{isArabic ? "لا يوجد منتجات" : "No products"}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
